/**
 * Редактор декларації платника єдиного податку (ч.1113) — повний бланк за
 * офіційною формою + вкладка «Додаток 1 (ЄСВ)» для ФОП. Поля редаговані
 * (жовтий фон), «перевірити» — арифметика і що звірити, «перерахувати з
 * обліку» — заново з доходу, «друк» — друкована форма пакета. Подання —
 * чернетка у Кабінеті ДПС (підпис КЕП і відправка там, пакетом з додатком).
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FORM_TITLES, PERIOD_LABELS, singleTaxDeclarations,
  type SingleTaxDeclCheck, type SingleTaxEsvAppendix, type SingleTaxFields, type SingleTaxMpzAppendix,
} from '@/api/singleTaxDeclarations';
import { orgs } from '@/api/catalogs';
import { useConfirm } from '@/components/ConfirmProvider';
import DatePopover from '@/components/DatePopover';
import {
  EsvAppendix, FopSectionI, FopSections, KNOWN_CODES, LegalSections, MpzAppendix,
  SingleTaxHeader, SingleTaxSignatures, computeMpz,
} from './SingleTaxBlank';

const GROUP: Record<string, number> = { FOP_1: 1, FOP_2: 2, FOP_3_3: 3, FOP_3_5: 3, SIMPLIFIED_3: 3 };

export default function SingleTaxDeclarationEditor() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const [values, setValues] = useState<Record<string, string>>({});
  const [fields, setFields] = useState<SingleTaxFields>({});
  const [esv, setEsv] = useState<SingleTaxEsvAppendix | null>(null);
  const [mpz, setMpz] = useState<SingleTaxMpzAppendix | null>(null);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'MAIN' | 'ESV' | 'MPZ'>('MAIN');
  const [check, setCheck] = useState<SingleTaxDeclCheck | null>(null);
  const [checking, setChecking] = useState(false);

  const docQ = useQuery({ queryKey: ['single-tax-declaration', id], queryFn: () => singleTaxDeclarations.get(id!), enabled: !!id });
  const d = docQ.data;
  const subQ = useQuery({ queryKey: ['single-tax-declaration-sub', id], queryFn: () => singleTaxDeclarations.submission(id!), enabled: !!id, refetchInterval: 30_000 });
  const orgListQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const org = orgListQ.data?.items.find((o) => o.id === d?.organization_id);

  useEffect(() => {
    if (d) {
      setValues(d.values ?? {}); setFields(d.fields ?? {});
      setEsv(d.appendix_data?.esv ?? null); setMpz(d.appendix_data?.mpz ?? null); setDirty(false);
    }
  }, [d]);

  const subStatus = subQ.data?.status ?? null;
  const readOnly = !!subStatus && !['DRAFT', 'ERROR'].includes(subStatus);
  const legal = d?.form_code === 'J0103509';
  const group = org ? (GROUP[org.tax_system] ?? null) : null;

  const payload = () => ({ values, fields,
    ...((esv || mpz) ? { appendix_data: { ...(esv ? { esv } : {}), ...(mpz ? { mpz } : {}) } } : {}) });
  const save = useMutation({
    mutationFn: () => singleTaxDeclarations.update(id!, payload()),
    onSuccess: () => { setDirty(false); setError(null); setCheck(null); qc.invalidateQueries({ queryKey: ['single-tax-declaration', id] }); },
    onError: (e: any) => setError(e?.response?.data?.detail ?? String(e)),
  });
  const recalc = useMutation({
    mutationFn: () => singleTaxDeclarations.recalc(id!),
    onSuccess: () => { setError(null); setCheck(null); qc.invalidateQueries({ queryKey: ['single-tax-declaration', id] }); },
    onError: (e: any) => setError(e?.response?.data?.detail ?? String(e)),
  });
  const remove = useMutation({
    mutationFn: () => singleTaxDeclarations.remove(id!),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['single-tax-declarations'] }); navigate('/taxation/single-tax-declarations'); },
    onError: (e: any) => setError(e?.response?.data?.detail ?? String(e)),
  });
  const saveDocDate = useMutation({
    mutationFn: (ymd: string) => singleTaxDeclarations.update(id!, { document_date: `${ymd}T00:00:00Z` }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['single-tax-declaration', id] }),
    onError: (e: any) => setError(e?.response?.data?.detail ?? String(e)),
  });

  async function runCheck() {
    if (!id) return;
    setChecking(true);
    try {
      if (dirty && !readOnly) await save.mutateAsync();
      setCheck(await singleTaxDeclarations.check(id));
    } catch (e: any) { setError(e?.response?.data?.detail ?? String(e)); } finally { setChecking(false); }
  }
  async function openPrint() {
    if (dirty && !readOnly) await save.mutateAsync();
    navigate(`/taxation/single-tax-declarations/${id}/print`);
  }

  const setV = (code: string, raw: string) => {
    setValues((v) => { const n = { ...v }; if (raw === '' || Number(raw) === 0) delete n[code]; else n[code] = raw; return n; });
    setDirty(true);
  };
  const onFields = (patch: Partial<SingleTaxFields>) => { setFields((f) => ({ ...f, ...patch })); setDirty(true); };
  const onEsv = (next: SingleTaxEsvAppendix) => {
    setEsv(next); setDirty(true);
    // р.21 = «Усього» графи 4 (сервер теж перерахує при збереженні).
    const total = next.include ? next.rows.reduce((s, r) => s + (Number(r.amount) || 0), 0) : 0;
    setValues((v) => { const n = { ...v }; if (total > 0) n.R021G3 = total.toFixed(2); else delete n.R021G3; return n; });
  };
  const otherCodes = useMemo(() => Object.keys(values).filter((c) => !KNOWN_CODES.has(c)).sort(), [values]);
  // ч.1114: р.04 Додатка 2 → р.14.2 (ФОП) / р.11 (юрособа); сервер перераховує те саме при збереженні.
  const onMpz = (next: SingleTaxMpzAppendix) => {
    setMpz(next); setDirty(true);
    if (!d) return;
    const isLegal = d.form_code === 'J0103509';
    const pos = next.include && next.plots.length ? computeMpz(next, d.period_year, isLegal).positive : 0;
    const col = org?.is_vat_payer ? 'G3' : 'G4';
    setValues((v) => {
      const nv = { ...v };
      const num = (c: string) => Number(nv[c] ?? 0) || 0;
      if (isLegal) {
        if (pos > 0) nv[`R011${col}`] = pos.toFixed(2); else delete nv[`R011${col}`];
        const r12 = num(`R010${col}`) + pos;
        if (r12) nv[`R012${col}`] = r12.toFixed(2); else delete nv[`R012${col}`];
      } else {
        if (pos > 0) nv.R0142G3 = pos.toFixed(2); else delete nv.R0142G3;
        const r14 = num('R0141G3') + pos;
        if (r14) nv.R014G3 = r14.toFixed(2); else delete nv.R014G3;
      }
      return nv;
    });
  };

  if (!d) return <div className="card">Завантаження…</div>;
  const title = `${legal ? 'Декларація єдинника (юрособа, 3 група)' : 'Декларація єдинника (ФОП)'} — ${PERIOD_LABELS[d.period_quarter]} ${d.period_year}`;

  return (
    <div className="card">
      <div className="toolbar" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>{title}</h2>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
          <button onClick={() => navigate('/taxation/single-tax-declarations')}>до списку</button>
          {!readOnly && (
            <>
              <button className="primary" onClick={() => save.mutate()} disabled={save.isPending || !dirty}>зберегти</button>
              <button onClick={runCheck} disabled={checking}>{checking ? '…' : 'перевірити'}</button>
              <button disabled={recalc.isPending} onClick={() => {
                confirm('Перерахувати з обліку? Ручні правки рядків і Додатка 1 буде перезаписано.')
                  .then((ok) => { if (ok) recalc.mutate(); });
              }}>перерахувати з обліку</button>
              <button className="danger" onClick={() => { confirm('Видалити декларацію?').then((ok) => { if (ok) remove.mutate(); }); }}>видалити</button>
            </>
          )}
          <button onClick={openPrint} title="Друкована форма декларації (і Додатка 1, якщо є)">друк</button>
        </div>
      </div>

      <div style={{ background: 'var(--primary-soft)', border: '1px solid var(--badge-blue-bd)', color: 'var(--badge-blue-fg)',
        padding: '0.5rem 0.75rem', borderRadius: 6, marginBottom: '0.75rem', display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <span>
          {subStatus === 'ACCEPTED' ? '✓ прийнято ДПС (квитанція №2)'
            : subStatus === 'REJECTED' ? '✗ відхилено ДПС'
            : subStatus === 'DELIVERED' ? '✓ доставлено (кв.1), очікуємо кв.2'
            : subStatus === 'SENT' ? '→ відправлено у ДПС'
            : subStatus === 'SIGNED' ? 'підписано — лишилося відправити у Кабінеті ДПС'
            : 'збережено чернеткою у Кабінеті ДПС — підпишіть і відправте там'}
        </span>
        <a style={{ cursor: 'pointer', marginLeft: 'auto' }} onClick={async () => {
          if (dirty && !readOnly) await singleTaxDeclarations.update(id!, payload());
          navigate('/dps-cabinet');
        }}>Кабінет ДПС →</a>
      </div>

      <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: 8, display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <span>{d.document_number} · {FORM_TITLES[d.form_code]} · суми у гривнях з копійками · дохід наростаючим підсумком з обліку, кожне поле можна виправити.</span>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4 }} title="Дата подання (D_FILL/HFILL)">
          Дата подання
          <div style={{ width: 130 }}>
            <DatePopover value={d.document_date?.slice(0, 10) ?? ''} onChange={(v) => { if (v && !readOnly) saveDocDate.mutate(v); }} />
          </div>
        </label>
      </div>
      {error && <div style={{ color: 'var(--badge-red-fg)', marginBottom: 8, whiteSpace: 'pre-wrap' }}>{error}</div>}

      {check && (
        <div style={{ marginBottom: '0.75rem' }}>
          {check.errors.length === 0 && check.warnings.length === 0 && (
            <div style={{ background: 'var(--badge-green-bg)', border: '1px solid var(--badge-green-bd)', color: 'var(--badge-green-fg)', padding: '0.5rem 0.75rem', borderRadius: 6 }}>
              ✓ Помилок не знайдено — декларація готова до подання{check.included_appendices.length ? ' (пакет: декларація + Додаток 1)' : ''}
            </div>
          )}
          {check.errors.length > 0 && (
            <div style={{ background: 'var(--badge-red-bg)', border: '1px solid var(--badge-red-bd)', color: 'var(--badge-red-fg)', padding: '0.5rem 0.75rem', borderRadius: 6, marginBottom: '0.5rem' }}>
              <b>Помилки ({check.errors.length}) — виправте перед поданням:</b>
              <ul style={{ margin: '0.3rem 0 0 1.1rem', padding: 0 }}>{check.errors.map((e, i) => <li key={i} style={{ fontSize: '0.85rem' }}>{e.message}</li>)}</ul>
            </div>
          )}
          {check.warnings.length > 0 && (
            <div style={{ background: 'var(--badge-amber-bg)', border: '1px solid var(--badge-amber-bd)', color: 'var(--badge-amber-fg)', padding: '0.5rem 0.75rem', borderRadius: 6 }}>
              <b>Перевірте ({check.warnings.length}):</b>
              <ul style={{ margin: '0.3rem 0 0 1.1rem', padding: 0 }}>{check.warnings.map((w, i) => <li key={i} style={{ fontSize: '0.85rem' }}>{w.message}</li>)}</ul>
            </div>
          )}
        </div>
      )}

      <div className="seg-tabs" style={{ marginBottom: '0.6rem' }}>
        <button className={tab === 'MAIN' ? 'active' : ''} onClick={() => setTab('MAIN')}>Декларація</button>
        {!legal && (
          <button className={tab === 'ESV' ? 'active' : ''} onClick={() => setTab('ESV')}>
            Додаток 1 (ЄСВ){esv?.include ? '' : ' — не подається'}
          </button>
        )}
        <button className={tab === 'MPZ' ? 'active' : ''} onClick={() => setTab('MPZ')}>
          {legal ? 'Додаток (МПЗ)' : 'Додаток 2 (МПЗ)'}{mpz?.include && mpz.plots.length ? '' : ' — не подається'}
        </button>
      </div>

      {tab === 'MAIN' && (
        <div>
          <SingleTaxHeader doc={d} org={org} fields={fields} onFields={onFields} readOnly={readOnly} />
          {legal
            ? <LegalSections values={values} setV={setV} readOnly={readOnly} />
            : (
              <>
                <FopSectionI fields={fields} onFields={onFields} readOnly={readOnly} />
                <FopSections values={values} fields={fields} setV={setV} onFields={onFields} readOnly={readOnly} group={group} />
              </>
            )}
          {otherCodes.length > 0 && (
            <div style={{ fontSize: '0.8rem', color: 'var(--muted)', marginTop: 6 }}>
              Інші заповнені поля: {otherCodes.map((c) => `${c}=${values[c]}`).join(', ')}
            </div>
          )}
          <SingleTaxSignatures doc={d} org={org} />
        </div>
      )}
      {tab === 'MPZ' && (
        <MpzAppendix doc={d} legal={legal} readOnly={readOnly}
          mpz={mpz ?? { include: false, plots: [], paid: {} }} onChange={onMpz} />
      )}
      {tab === 'ESV' && !legal && (
        <EsvAppendix doc={d} org={org} readOnly={readOnly}
          esv={esv ?? { include: false, rows: Array.from({ length: 12 }, (_, i) => ({ month: i + 1, base: '0', rate: '22', amount: '0' })), category_code: '6',
            period_from: `${d.period_year}-01-01`, period_to: `${d.period_year}-12-31` }}
          onChange={onEsv} />
      )}
    </div>
  );
}
